import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaLock, FaLockOpen, FaSort } from 'react-icons/fa6';
import { MdPhotoLibrary } from 'react-icons/md';
import classNames from 'classnames';
import PageHeader from '../../components/misc/PageHeader';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import SearchBar from '../../components/forms/SearchBar';
import DropdownSelect from '../../components/forms/DropdownSelect';
import SectionHeader from '../../components/misc/SectionHeader';
import photoAlbumService from '../../../services/community/photoAlbumService';
import ApiImage from '../../components/misc/ApiImage';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import { useDebounceEffect } from '../../../utils/useDebounceEffect';

interface PhotoAlbumCardProps {
  album: PhotoAlbum;
  onClick: () => void;
}

export default function PhotoAlbumsOverviewPage(): JSX.Element {
  const navigate = useNavigate();
  const [photoAlbums, setPhotoAlbums] = React.useState<PhotoAlbum[]>([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'title' | 'date' | 'photoCount'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const community = useSelectedCommunity();

  const sortOptions = [
    { label: t('page.albums.sort.date'), value: 'date' as const },
    { label: t('page.albums.sort.title'), value: 'title' as const },
    { label: t('page.albums.sort.photoCount'), value: 'photoCount' as const },
  ];

  const orderOptions = [
    { label: t('page.albums.sort.desc'), value: 'desc' as const },
    { label: t('page.albums.sort.asc'), value: 'asc' as const },
  ];

  const fetchAlbums = async () => {
    if (!community?.id) return;
    setIsLoading(true);
    try {
      const albums = await photoAlbumService.getPhotoAlbums(community.id, {
        search: search || undefined,
        sortBy,
        sortOrder,
      });
      setPhotoAlbums(albums);
    } catch (error) {
      console.error('Failed to fetch photo albums:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useDebounceEffect(
    () => {
      fetchAlbums();
    },
    300,
    [community?.id, search, sortBy, sortOrder],
  );

  const handleAlbumClick = (albumId: string) => {
    navigate(`/album/${albumId}`);
  };

  return (
    <>
      <PageHeader
        title={t('page.albums.title')}
        subtitle={t('page.albums.subtitle')}
        primaryButton={
          <PrimaryButton
            text={t('page.albums.createAlbum')}
            icon={FaPlus}
            onClick={() => navigate('/create-album')}
          />
        }
      />

      <div className="overflow-hidden rounded-[12px] border border-secondary-200 bg-surface">
        <SectionHeader
          title={t('page.albums.title')}
          count={photoAlbums.length}
          action={
            <div className="flex flex-wrap items-center gap-3">
              <SearchBar
                search={search}
                setSearch={setSearch}
                className="w-full sm:w-56"
                inputProps={{
                  placeholder: t('page.albums.search.placeholder'),
                }}
              />
              <div className="flex items-center gap-2">
                <FaSort className="h-4 w-4 text-primary-900" />
                <DropdownSelect
                  options={sortOptions}
                  value={sortBy}
                  setValue={(value) => value && setSortBy(value)}
                  placeholder={t('page.albums.sort.sortBy')}
                />
                <DropdownSelect
                  options={orderOptions}
                  value={sortOrder}
                  setValue={(value) => value && setSortOrder(value)}
                  placeholder={t('page.albums.sort.order')}
                />
              </div>
            </div>
          }
        />

        <div className="p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-primary-900">{t('general.loading')}...</div>
            </div>
          ) : photoAlbums.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {photoAlbums.map((album) => (
                <PhotoAlbumCard
                  key={album.id}
                  album={album}
                  onClick={() => handleAlbumClick(album.id)}
                />
              ))}
            </div>
          ) : (
            <EmptyScreenView
              title={search ? t('page.albums.empty.noResults') : t('page.albums.empty.title')}
              subtitle={
                search ? t('page.albums.empty.tryDifferentSearch') : t('page.albums.empty.subtitle')
              }
            />
          )}
        </div>
      </div>
    </>
  );
}

function PhotoAlbumCard({ album, onClick }: PhotoAlbumCardProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div
      onClick={onClick}
      className="group relative cursor-pointer rounded-[8px] bg-secondary-200/70 p-3 transition-colors hover:bg-secondary-200">
      <div className="relative mb-3 flex h-36 w-full items-center justify-center overflow-hidden rounded-[8px] bg-primary-100">
        <ApiImage
          path={album.coverUrl || '/assets/vera_smile.svg'}
          alt={album.title}
          className={classNames('h-full w-full transition-opacity group-hover:opacity-80', {
            'object-cover': album.coverUrl,
          })}
        />
        <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded-[8px] bg-primary-900/80 px-2 py-1 text-xs text-secondary-50">
          <MdPhotoLibrary className="h-3 w-3" />
          <span className="text-xs text-secondary-50">{album.photoCount}</span>
        </div>
      </div>

      <div className="space-y-1">
        <h3 className="line-clamp-1 font-semibold text-primary-900">{album.title}</h3>
        <p className="line-clamp-2 text-sm text-gray-500">{album.description}</p>

        <div className="flex items-center justify-between text-xs">
          <div
            className={`flex items-center gap-1 ${album.openToUpload ? 'text-success' : 'text-gray-500'}`}>
            {album.openToUpload ? (
              <>
                <FaLockOpen className="h-3 w-3" />
                <span>{t('page.albums.openUpload')}</span>
              </>
            ) : (
              <>
                <FaLock className="h-3 w-3" />
                <span>{t('page.albums.restrictedUpload')}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
